import { Component, input, viewChild, inject, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { EMPTY, firstValueFrom, Observable, Subscription } from 'rxjs';
import { Node, Document, Branch, NodeModel, DragDrop, RegistryEntry } from '@xanadu/model';
import { BaseMenu, MenuData, MenuBuilder } from '@xanadu/menu';
import { AuthSignal, CommonSignal, NoticeSignal, TreeSignal } from '@xanadu/signal';
import { switchMap, tap } from 'rxjs/operators';
import { ObjectHelper, RandomHelper } from '@xanadu/common';
import { MatMenuTrigger } from '@angular/material/menu';
import { DataService } from '@xanadu/service';
import { TabSignal, TabSignalSet } from '@xanadu/signal';
import { Consent } from '@xanadu/support';

@Component({
    selector: 'node-tree',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class TreeComponent<T extends Node> {
    protected readonly location = inject(Location);
    protected readonly randomHelper = inject(RandomHelper);
    protected readonly objectHelper = inject(ObjectHelper);

    protected readonly dataService = inject(DataService);
    protected readonly tabSignal = inject(TabSignal);

    protected readonly authSignal = inject(AuthSignal);
    protected readonly treeSignal = inject(TreeSignal);
    protected readonly commonSignal = inject(CommonSignal);
    protected readonly noticeSignal = inject(NoticeSignal);

    readonly model = input<any>();

    readonly menuTrigger = viewChild(MatMenuTrigger);

    readonly rootModel = signal<NodeModel | undefined>(undefined);

    protected abstract menuBuilder?: MenuBuilder;
    private topicDelete = 'delete';
    protected registry: Map<string, RegistryEntry> = new Map<string, RegistryEntry>();

    current?: NodeModel;
    clipboard?: NodeModel;
    nodeCut?: NodeModel;
    menuData?: MenuData;

    subscription?: Subscription;
    menuPosition = { x: 0, y: 0 };

    loadBatch: boolean = false;

    private ids?: string[];

    protected get nodeType(): string | undefined {
        return undefined;
    }

    constructor(
    ) {
        effect(() => {
            const model = this.model();

            if (model) {
                const root = new NodeModel(model.root, this.loadBatch);
                this.rootModel.set(root);
                // load all children, viewModel is the NodeModel for root node.
                root.load();
                this.tabSignal.load[this.nodeType as keyof TabSignalSet<NodeModel>].set(root);
            }
        });

        effect(() => {
            const reply = this.commonSignal.reply();

            if (reply?.target === this) {
                if (reply.topic === this.topicDelete) {
                    if (reply.result.isOk) {
                        this.deleteNode(reply.data.model as NodeModel);
                    }
                }
            }
        });

        effect(() => {
            const entry = this.tabSignal.enter[this.nodeType as keyof TabSignalSet<RegistryEntry>]();

            if (entry?.id) {
                this.registry.set(entry.id, entry);
                this.go(entry);
            }
        });

        effect(() => {
            const id = this.tabSignal.erase[this.nodeType as keyof TabSignalSet<string>]();

            if (id?.length) {
                this.registry.delete(id);
            }
        });


        effect(() => {
            const query = this.tabSignal.path[this.nodeType as keyof TabSignalSet<any>]();

            if (query?.id && !query.id.startsWith('_')) {
                const entry = this.registry.get(query.id);


                if (entry) {
                    this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>].set(entry.model);
                } else {
                    void this.locate(query.id);
                }
            }
        });

        effect(() => {
            const type = this.nodeType as keyof TabSignalSet<string[]>;
            const ids = this.tabSignal.seek[type]();

            if (ids?.length) {
                this.ids = ids;
                void this.seek();
            }
        });

        effect(() => {
            const menuTrigger = this.menuTrigger();
            const menuData = this.tabSignal.menu[this.nodeType as keyof TabSignalSet<MenuData>]();

            if (menuTrigger && menuData?.viewModel?.node && menuData.position) {
                this.menuData = menuData;

                const menu = this.buildMenu(menuData.viewModel.node);
                this.menuPosition = menuData.position;
                menuTrigger.menuData = { menu, ...menuData };
                menuTrigger.openMenu();
            }
        });

        effect(() => {
            const model = this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>]();

            this.current?.actify(false);
            model?.actify(true);
            this.current = model;
        });

        effect(() => {
            const model = this.tabSignal.replace[this.nodeType as keyof TabSignalSet<NodeModel>]();

            if (model) {
                void firstValueFrom(this.move(model, this.current));
            }
        });

        effect(() => {
            const model = this.tabSignal.drop[this.nodeType as keyof TabSignalSet<DragDrop>]();

            if (model) {
                const drop = this.registry.get(model.dropId)?.model;
                if (drop && !drop.node.isNew && this.dropMatch(model.drag, drop)) {
                    void firstValueFrom(this.diverse(model.drag, drop)).then(_ => {
                        this.clipboard = this.nodeCut = undefined;
                    });
                }
            }
        });

        effect(() => {
            const document = this.tabSignal.reset[this.nodeType as keyof TabSignalSet<Document>]();

            // triggred when node is saved, id has value only if node is new, otherwise id is undefined.
            const id = document?._?.length ? document._ : document?._id;
            const entry = id ? this.registry.get(id) : undefined;
            if (entry && id) {
                if (document?._?.length) {
                    this.registry.delete(id);
                    if (entry.id) {
                        this.registry.set(entry.id, entry);
                    }
                }

                entry.model.reset(this.current?.node._id === entry.model.node._id);
            }
        });
    }

    onDelete(model?: NodeModel) {
        const query: Consent = {
            target: this,
            topic: this.topicDelete,
            component: 'okay',
            style: '{"width":"480px","height":"360px"}',
            data: {
                model,
                content: 'Do you want to delete?',
                ok: 'Yes',
                cancel: 'No',
            }
        };

        this.commonSignal.consent.set(query);
    }

    onCut(model?: NodeModel) {
        this.nodeCut = this.clipboard = model;
        this.tabSignal.replace[this.nodeType as keyof TabSignalSet<NodeModel>].set(undefined);
    }

    onCopy(model?: NodeModel) {
        this.clipboard = model;
        this.nodeCut = undefined;
        this.tabSignal.replace[this.nodeType as keyof TabSignalSet<NodeModel>].set(undefined);
    }

    paste() {
        if (this.nodeCut) {
            if (this.clipboard) {
                this.tabSignal.replace[this.nodeType as keyof TabSignalSet<NodeModel>].set(this.clipboard);
            }
            this.clipboard = this.nodeCut = undefined;
        } else {
            if (this.clipboard) {
                const copy = this.objectHelper.clone(this.clipboard.node) as Node;
                if (copy instanceof Branch) {
                    copy.nodes = [];
                    copy.branches = [];
                }
                copy._id = undefined;
                this.addChild(copy);
            }
        }
    }

    protected diverse(model: NodeModel, branchModel: NodeModel): Observable<T> {
        if (!branchModel) {
            return EMPTY;
        }

        if (branchModel.loaded) {
            return this.move(model, branchModel)
        } else {
            return branchModel.node._id
                ? this.dataService.getNode(branchModel.node._id).pipe(
                    tap(node => branchModel.load(node)),
                    switchMap(_ => {
                        this.treeSignal.contentLoad.set(branchModel);
                        return this.move(model, branchModel);
                    }))
                : EMPTY;
        }
    }

    protected move(model: NodeModel, branchModel?: NodeModel): Observable<T> {
        const branch = branchModel?.info.branch;
        if (branch && (branch?.nodes || branch?.branches)) {
            return this.replace(model.node, branch)
                .pipe(tap(_ => {
                    model.moved = true;
                    model.replace(branchModel);
                }));
        }
        return EMPTY;
    }

    protected dropMatch(dragModel: NodeModel, dropModel: NodeModel): boolean {
        if (!dropModel.solo.canDrop || dropModel === dragModel.parent) {
            return false;
        }

        // check to see if drop node is child of drag node
        for (let model = dropModel; model.parent; model = model.parent) {
            if (model.node._id === dragModel.node._id) {
                return false;
            }
        }

        return this.dropOk(dragModel, dropModel);
    }

    protected scrollTo(id: string) {
        setTimeout(() => {
            const element = this.registry.get(id)?.element;
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                void this.seek();
            }
        }, 0);
    }

    protected deleteNode(model: NodeModel): void {
        if (model) {
            this.tabSignal.delete[this.nodeType as keyof TabSignalSet<NodeModel>].set(model);
        }
    }

    protected addChild(node: Node, silent: boolean = false, insert?: (parent: NodeModel, child: NodeModel) => void): void {
        if (this.current && node) {
            node.parentId = this.current.node._id;
            node.parent = this.current.node as Branch;
            node.newId(this.randomHelper.generate());

            const nodeModel = this.current.add(node, silent, insert);

            if (this.menuData?.viewModel?.solo) {
                this.menuData.viewModel.solo.expanded = true;
            }

            if (node._id && !silent) {
                //signal load to register the child element for scrolling to it later.
                this.tabSignal.load[this.nodeType as keyof TabSignalSet<NodeModel>].set(nodeModel);
                this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>].set(nodeModel);
                this.location.go(`${this.nodeType}/id/${node._id}`);
                this.scrollTo(node._id);
            }
        }
    }

    protected abstract replace(node: Node, branch: Branch): Observable<T>;
    protected abstract dropOk(dragModel: NodeModel, dropModel: NodeModel): boolean;

    private buildMenu(node: Node): BaseMenu | undefined {
        return this.menuBuilder?.Build(node, this.clipboard?.node);
    }

    private async locate(id: string): Promise<void> {
        const ids = await firstValueFrom(this.dataService.locate(id));

        if (ids) {
            this.tabSignal.seek[this.nodeType as keyof TabSignalSet<string[]>].set(ids);
        }
    }

    private async seek(): Promise<void> {
        const id = this.ids?.at(0);
        if (id) {
            const entry = this.registry.get(id);
            if (entry) {
                this.go(entry);
                this.ids?.shift();
            } else if (this.current) {
                const model = this.current.children?.find(x => x.node._id === id) || this.current.descents?.find(x => x.node._id === id);
                if (model) {
                    this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>].set(model);
                }
            }
        }
    }

    private go(entry: RegistryEntry): void {
        const type = this.nodeType as keyof TabSignalSet<NodeModel>;
        this.tabSignal.node[type].set(entry?.model);

        // Wait until Angular has had an opportunity to update the DOM.
        if (entry?.id) {
            this.scrollTo(entry.id!);
        }
    }
}
